import { create } from "zustand";
import {
  fetchApps,
  fetchBoard,
  fetchCreatives,
  fetchState,
  putOptimization,
  sortCreatives,
  OptimizationKey,
  OptimizeApp,
  OptimizeBoard,
  OptimizeCreative,
  OptimizeStateEntry,
  Platform,
  PutOptimizationBody,
} from "./api";
import { OptimizeGame } from "./data";

// Server-backed state for the whole Optimize tab: the partner's titles, the card
// states and the per-game board. There is no local fallback — a failing call
// surfaces as `error`, because stand-in data on a broken API is how you end up
// trusting a screen that isn't real.

export type OptimizeMode = "loading" | "live" | "error";

const gameKey = (appId: string, platform: Platform) => `${platform}:${appId}`;

// parse a live game id back into (appId, platform)
export const parseGameId = (
  id: string
): { appId: string; platform: Platform } | null => {
  const sep = id.indexOf(":");
  if (sep === -1) return null;
  const platform = id.slice(0, sep);
  if (platform !== "android" && platform !== "ios") return null;
  return { appId: id.slice(sep + 1), platform };
};

const GRADIENTS = [
  "linear-gradient(135deg, #F6AD55 0%, #ED8936 100%)",
  "linear-gradient(135deg, #9F7AEA 0%, #6B46C1 100%)",
  "linear-gradient(135deg, #4FD1C5 0%, #319795 100%)",
  "linear-gradient(135deg, #63B3ED 0%, #3182CE 100%)",
  "linear-gradient(135deg, #68D391 0%, #38A169 100%)",
  "linear-gradient(135deg, #FC8181 0%, #C53030 100%)",
];

// Store listings pad the title with a tagline after a separator ("Eventor:
// Konser Arkadaşı Bul!", "PLAYTIME - Earn Money Playing"). Cut at the first
// separator so the UI shows the bare title.
//
// Colon-family marks are cut wherever they appear because listings attach them
// to the title; dash-family marks only count when they stand alone as a word,
// so "Co-op Master" and "Mini-Golf" survive intact. A name that is nothing but
// a separator prefix (or has none) is returned untouched.
const NAME_SEPARATORS = /\s*[:|｜•·‧¦]|\s+[-–—~/](?=\s|$)/;

export const shortenAppName = (name: string): string => {
  const cut = name.split(NAME_SEPARATORS)[0].trim();
  return cut.length > 0 ? cut : name.trim();
};

// adapt a live app to the shape the previews / avatars / strip already consume.
// appName and iconUrl are null for titles with no store metadata yet — fall back
// to the package name and to the avatar's gradient+initials.
export const appToGame = (app: OptimizeApp, index: number): OptimizeGame => ({
  id: gameKey(app.appId, app.platform),
  name: app.appName
    ? shortenAppName(app.appName)
    : app.packageName ?? app.appId,
  platform: app.platform === "ios" ? "iOS" : "Android",
  icon: app.iconUrl ?? "",
  gradient: GRADIENTS[index % GRADIENTS.length],
});

interface OptimizeLiveStore {
  mode: OptimizeMode;
  // set when the backend didn't answer
  loadError: string | null;
  apps: OptimizeApp[];
  games: OptimizeGame[]; // apps adapted for the preview / avatar components
  states: OptimizeStateEntry[];
  boards: Record<string, OptimizeBoard>; // keyed by `${platform}:${appId}`
  boardLoading: string | null;
  // partner creative overrides per game, shared by the upload card and the
  // preview so one fetch serves both. Their `url`s are presigned S3 links valid
  // for ~1 hour — this is a per-visit cache, never persist it.
  creatives: Record<string, OptimizeCreative[]>;
  creativesError: Record<string, boolean>;
  // pass force to retry after a failure
  init: (force?: boolean) => Promise<void>;
  loadBoard: (appId: string, platform: Platform) => Promise<void>;
  // pass force to refetch (after an upload/delete, or to retry a failure)
  loadCreatives: (
    appId: string,
    platform: Platform,
    force?: boolean
  ) => Promise<void>;
  // PUT a card change; the returned board replaces the cached one and its
  // entries are folded into `states` so the overview stays in sync
  saveCard: (
    key: OptimizationKey,
    body: PutOptimizationBody
  ) => Promise<OptimizeBoard>;
  // back to the untouched state — called on logout so the next account's
  // visit refetches instead of showing the previous partner's games
  reset: () => void;
}

// creatives fetches in flight, so the card and the preview mounting together
// don't fire the same request twice
const creativesInFlight = new Set<string>();

export const useOptimizeLive = create<OptimizeLiveStore>()((set, get) => ({
  mode: "loading",
  loadError: null,
  apps: [],
  games: [],
  states: [],
  boards: {},
  boardLoading: null,
  creatives: {},
  creativesError: {},

  init: async (force = false) => {
    if (!force && get().mode !== "loading") return;
    set({ mode: "loading", loadError: null });
    try {
      const apps = await fetchApps();
      const states = await fetchState().catch(() => []);
      set({
        mode: "live",
        loadError: null,
        apps,
        games: apps.map(appToGame),
        states,
      });
    } catch (error) {
      set({
        mode: "error",
        loadError:
          error instanceof Error ? error.message : "couldn't reach the API",
      });
    }
  },

  loadBoard: async (appId, platform) => {
    const key = gameKey(appId, platform);
    if (get().boards[key] || get().boardLoading === key) return;
    set({ boardLoading: key });
    try {
      const board = await fetchBoard(appId, platform);
      set((state) => ({ boards: { ...state.boards, [key]: board } }));
    } catch {
      // board fetch failure leaves the panel in its loading-failed state
    } finally {
      set({ boardLoading: null });
    }
  },

  loadCreatives: async (appId, platform, force = false) => {
    const key = gameKey(appId, platform);
    if (creativesInFlight.has(key)) return;
    if (!force && get().creatives[key]) return;
    creativesInFlight.add(key);
    try {
      const creatives = sortCreatives(await fetchCreatives(appId, platform));
      set((state) => ({
        creatives: { ...state.creatives, [key]: creatives },
        creativesError: { ...state.creativesError, [key]: false },
      }));
    } catch {
      // keep any stale list we have; the error flag lets the card distinguish
      // "couldn't load" from the normal empty starting state
      set((state) => ({
        creativesError: { ...state.creativesError, [key]: true },
      }));
    } finally {
      creativesInFlight.delete(key);
    }
  },

  saveCard: async (key, body) => {
    const board = await putOptimization(key, body);
    const k = gameKey(body.appId, body.platform);
    set((state) => ({
      boards: { ...state.boards, [k]: board },
      states: mergeStates(state.states, board, body.appId, body.platform),
    }));
    return board;
  },

  reset: () => {
    creativesInFlight.clear();
    set({
      mode: "loading", // init()'s guard lets a "loading" store fetch again
      loadError: null,
      apps: [],
      games: [],
      states: [],
      boards: {},
      boardLoading: null,
      creatives: {},
      creativesError: {},
    });
  },
}));

// replace this app's entries in /state with the fresh board's entries
const mergeStates = (
  states: OptimizeStateEntry[],
  board: OptimizeBoard,
  appId: string,
  platform: Platform
): OptimizeStateEntry[] => {
  const kept = states.filter(
    (entry) => !(entry.appId === appId && entry.platform === platform)
  );
  const fromBoard: OptimizeStateEntry[] = board.cards.flatMap((card) =>
    card.entries
      // configured-but-never-activated slots (params saved, still disabled)
      // stay in: they are what the titles list flags as "Needs activation" —
      // per Günay, adding a challenge gave no sign it still needs activating
      .filter(
        (entry) =>
          entry.status !== "disabled" || entry.activatedAt || entry.params
      )
      .map((entry) => ({
        optimization: card.key,
        appId,
        platform,
        slot: entry.slot,
        status: entry.status,
        activatedAt: entry.activatedAt,
      }))
  );
  return [...kept, ...fromBoard];
};

// Titles the card previews fill their multi-game slots from — the partner's own,
// never anyone else's; short placements are padded with placeholders instead.
// Read outside React on purpose: previews re-render through their `game` prop,
// which already comes from a subscribed selector.
export const previewPool = (): OptimizeGame[] =>
  useOptimizeLive.getState().games;

export const useOptimizeGames = (): OptimizeGame[] =>
  useOptimizeLive((state) => state.games);

// the app behind a game id, for the per-app fields on /apps (eligibility,
// active challenge count, event prefills)
export const useApp = (gameId: string): OptimizeApp | undefined =>
  useOptimizeLive((state) => {
    const parsed = parseGameId(gameId);
    if (!parsed) return undefined;
    return state.apps.find(
      (app) => app.appId === parsed.appId && app.platform === parsed.platform
    );
  });

// this game's creative overrides (undefined until loaded) and whether the last
// fetch failed — read together with loadCreatives. Two subscriptions so the
// selector output stays referentially stable between store updates.
export const useCreatives = (
  gameId: string
): { creatives: OptimizeCreative[] | undefined; failed: boolean } => {
  const creatives = useOptimizeLive((state) => state.creatives[gameId]);
  const failed = useOptimizeLive((state) =>
    Boolean(state.creativesError[gameId])
  );
  return { creatives, failed };
};

// this card's state entries for a game id, from /state
export const useCardState = (cardKey: string, gameId: string) =>
  useOptimizeLive((state) => {
    const parsed = parseGameId(gameId);
    if (!parsed) return undefined;
    return state.states
      .filter(
        (entry) =>
          entry.optimization === cardKey &&
          entry.appId === parsed.appId &&
          entry.platform === parsed.platform
      )
      .sort((a, b) => a.slot - b.slot);
  });
